/**
 * V108-6-4 — "Hand the N repo steps to your coding agent": one closed line above the step list. Open, it
 * shows (1) the MCP add command for the pinned CLI with a sign-in line and (2) a prompt limited to the
 * coding-agent steps, one pull request per repo, in dependency order. When no CLI version is published
 * yet it says so plainly and hands over the steps' own commands instead (no unpinned MCP claim).
 */
import { useEffect, useRef } from 'react';
import { CodeBlock } from '@aigovops/design-system';
import { GACPRO_CLI_PACKAGE } from '@aigovops/core';
import type { EnablerItem } from '../../lib/setup-api';

export function AgentBox({ steps, workspaceName, cliVersion, openOnLoad }: { steps: readonly EnablerItem[]; workspaceName: string; cliVersion: string | null; openOnLoad: boolean }) {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => { if (openOnLoad && ref.current) { ref.current.open = true; ref.current.scrollIntoView?.({ block: 'start' }); } }, [openOnLoad]);
  if (!steps.length) return null;
  const n = steps.length;
  const pkg = cliVersion ? `${GACPRO_CLI_PACKAGE}@${cliVersion}` : null;
  const prompt = `Use the GAC Pro tools to work through ${workspaceName}'s coding-agent setup steps, in dependency order. Open one pull request per repo. Don't run cloud or console commands, and don't merge anything.`;
  const fallback = `Work through these GAC Pro setup steps for ${workspaceName}, in order, one pull request per repo. Don't run cloud or console commands, and don't merge anything.\n${steps.map((s, i) => `${i + 1}. ${s.title}${s.instructions.command ? `: ${s.instructions.command}` : ''}`).join('\n')}`;
  return (
    <details ref={ref} id="agent" className="su-agent">
      <summary><span>Hand the {n} repo {n === 1 ? 'step' : 'steps'} to your coding agent</span><span className="su-tog" aria-hidden="true" /></summary>
      <div className="su-agent-body">
        {pkg ? (
          <ol className="su-agent-steps">
            <li>
              <span className="su-lbl">1. Add GAC Pro to Claude Code (once)</span>
              <CodeBlock label="The add-tools command" code={`claude mcp add -s user gacpro -- npx ${pkg} mcp`} />
              <p className="su-hint">First run asks you to sign in. It can read setup steps and report pull requests, nothing else.</p>
              <details className="su-more"><summary>How sign-in works</summary><ul>
                <li>Sign-in opens GAC Pro in your browser and gives this machine a token for setup steps only.</li>
                <li>The token stays in your environment; the agent never writes it to a file.</li>
              </ul></details>
              <details className="su-more"><summary>Other agents</summary><ul>
                <li>Cursor, Codex or any MCP client: add a server that runs <span className="su-mono">npx {pkg} mcp</span>.</li>
                <li>No MCP? Your agent can run the command in each step directly.</li>
              </ul></details>
            </li>
            <li>
              <span className="su-lbl">2. Paste this prompt</span>
              <CodeBlock label="The prompt" code={prompt} />
            </li>
          </ol>
        ) : (
          <>
            <p className="su-hint">The GAC Pro CLI isn't published yet, so your agent can't read these steps itself. Paste the steps instead.</p>
            <CodeBlock label="The prompt with the steps" code={fallback} />
          </>
        )}
        <p className="su-hint">Pull requests use your agent's own Git access. GAC Pro never gets write access to your code.</p>
      </div>
    </details>
  );
}
